import * as React from "react";
import { Avatar as AvatarPrimitive } from "@base-ui/react/avatar";

import { cn } from "../../lib/utils";

function Avatar({
  className,
  radius = "full",
  size = "default",
  ...props
}: AvatarPrimitive.Root.Props & {
  radius?: "none" | "sm" | "md" | "lg" | "full";
  size?: "xs" | "sm" | "default" | "lg";
}) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(
        "group/avatar relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden bg-primary text-text-always-white align-middle font-medium",
        radius === "none" ? "rounded-none" : radius === "sm" ? "rounded-sm" : radius === "md" ? "rounded-md" : radius === "lg" ? "rounded-lg" : "rounded-full",
        size === "xs" ? "size-5 text-caption" : size === "sm" ? "size-6 text-caption" : size === "lg" ? "size-9 text-sm" : "size-8 text-xs",
        className,
      )}
      {...props}
    />
  );
}

function AvatarImage({ className, ...props }: AvatarPrimitive.Image.Props) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn("size-full object-cover", className)}
      {...props}
    />
  );
}

function AvatarFallback({
  className,
  ...props
}: AvatarPrimitive.Fallback.Props) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        "flex size-full items-center justify-center bg-primary text-text-always-white",
        className,
      )}
      {...props}
    />
  );
}

function AvatarBadge({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="avatar-badge"
      className={cn(
        "[position:absolute] [right:0rem] [bottom:0rem] [z-index:10] [display:inline-flex] [align-items:center] [justify-content:center] [border-radius:9999px] [background-color:var(--primary)] [color:var(--color-text-always-white)] [outline-width:1px] [outline-style:solid] [outline-color:var(--background)] [user-select:none]",
        className,
      )}
      {...props}
    />
  );
}

function AvatarGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="avatar-group"
      className={cn(
        "group/avatar-group",
        "flex items-center [&>[data-slot=avatar]:not(:first-child),&>[data-slot=avatar-group-count]:not(:first-child)]:-ml-2 [&>[data-slot=avatar],&>[data-slot=avatar-group-count]]:bg-background [&>[data-slot=avatar],&>[data-slot=avatar-group-count]]:ring-1 [&>[data-slot=avatar],&>[data-slot=avatar-group-count]]:ring-background",
        className,
      )}
      {...props}
    />
  );
}

function AvatarGroupCount({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="avatar-group-count"
      className={cn(
        "[position:relative] [display:flex] [width:2rem] [height:2rem] [flex-shrink:0] [align-items:center] [justify-content:center] [border-radius:9999px] [background-color:var(--muted)] [font-size:0.875rem] [line-height:1.25rem] [color:var(--muted-foreground)] [outline-width:1px] [outline-style:solid] [outline-color:var(--background)] [&>svg]:[width:1rem] [&>svg]:[height:1rem]",
        className,
      )}
      {...props}
    />
  );
}

export {
  Avatar,
  AvatarImage,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
  AvatarBadge,
  AvatarPrimitive,
};
